// Editable rating cell for the New Team Change view's talent grid.
// Clicking opens a 4-option green-ramp picker (Apprentice / Competent /
// Proficient / Expert) plus a Clear option to revert to the baseline.
// Cells that carry a user-set override get a dashed primary outline so
// pending changes are visible at a glance.

import type { RatingValue } from '@lib/api-types/ratings';
import { useEffect, useRef, useState } from 'react';


import { RATING_LABELS } from '../../data-views.ts';

const LEVELS: RatingValue[] = [1, 2, 3, 4];

export function TeamChangeRatingCell({
  baselineRating,
  override,
  isOverlay,
  isAgent,
  isDeparted,
  gridRow,
  gridColumn,
  skillDisplay,
  onSet,
  availableFunctions,
  assignedFunctionIds,
  onToggleFunction,
}: {
  baselineRating: RatingValue | null;
  /** `undefined` = no override (cell shows baseline). A numeric value
   *  is the user-set level — passing `null` to `onSet` clears it. */
  override: RatingValue | undefined;
  isOverlay: boolean;
  isAgent: boolean;
  isDeparted: boolean;
  gridRow: number;
  gridColumn: number;
  skillDisplay: string;
  onSet: (next: RatingValue | null) => void;
  /** Full function list shown in the "Assign functions" sub-view. */
  availableFunctions?: Array<{ id: string; display: string }>;
  /** Function ids currently assigned to this (engineer, skill) pair. */
  assignedFunctionIds?: Set<string>;
  /** Toggle a single function assignment on/off. */
  onToggleFunction?: (functionId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'levels' | 'functions'>('levels');
  const wrapRef = useRef<HTMLDivElement>(null);

  // Reset to the level view every time the popover closes so re-opens
  // start from the primary menu, not whatever sub-view the user left it
  // on last time.
  useEffect(() => {
    if (!open) setView('levels');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const effective = override ?? baselineRating;
  const modified = override !== undefined;

  const cellClasses = ['snap-cell', 'snap-req-cell__btn'];
  if (effective === null) cellClasses.push('green', 'na');
  else cellClasses.push('green', `lvl-${effective}`);
  if (isAgent) cellClasses.push('is-agent');
  if (isDeparted) cellClasses.push('is-departed');
  if (isOverlay) cellClasses.push('is-overlay-cell');
  if (modified) cellClasses.push('is-modified');

  const titleParts = [skillDisplay];
  if (effective !== null) titleParts.unshift(RATING_LABELS[effective]);
  if (modified) {
    const base = baselineRating
      ? RATING_LABELS[baselineRating]
      : 'no rating';
    titleParts.push(`Edited (was ${base})`);
  }

  return (
    <div
      ref={wrapRef}
      className='snap-req-cell'
      style={{ gridRow, gridColumn }}
    >
      <button
        type='button'
        className={cellClasses.join(' ')}
        onClick={() => setOpen(v => !v)}
        aria-haspopup='true'
        aria-expanded={open}
        title={titleParts.join(' · ')}
      />
      {open ? (
        <div className='snap-req-picker' role='menu'>
          {view === 'levels' ? (
            <>
              {/* Mirrors the create-project flow: once a level is set,
               *  surface "Assign functions" at the top so the user can
               *  scope which BFs this (IC, skill) pairing covers. The
               *  implicit default (no override) is "all functions that
               *  intersect this skill's comp". */}
              {effective !== null &&
              availableFunctions &&
              onToggleFunction ? (
                <button
                  type='button'
                  className='snap-req-picker__opt snap-req-picker__opt--functions'
                  role='menuitem'
                  onClick={() => setView('functions')}
                >
                  <span
                    className='snap-req-picker__swatch is-functions'
                    aria-hidden
                  >
                    <svg
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <rect x='3' y='3' width='7' height='7' />
                      <rect x='14' y='3' width='7' height='7' />
                      <rect x='3' y='14' width='7' height='7' />
                      <rect x='14' y='14' width='7' height='7' />
                    </svg>
                  </span>
                  <span>Assign functions…</span>
                </button>
              ) : null}
              {LEVELS.map(lvl => (
                <button
                  key={lvl}
                  type='button'
                  className='snap-req-picker__opt'
                  role='menuitem'
                  onClick={() => {
                    onSet(lvl);
                    setOpen(false);
                  }}
                >
                  <span
                    className={`snap-req-picker__swatch snap-cell green lvl-${lvl}`}
                    aria-hidden
                  />
                  <span>{RATING_LABELS[lvl]}</span>
                </button>
              ))}
              {modified ? (
                <button
                  type='button'
                  className='snap-req-picker__opt snap-req-picker__opt--clear'
                  role='menuitem'
                  onClick={() => {
                    onSet(null);
                    setOpen(false);
                  }}
                >
                  <span
                    className='snap-req-picker__swatch is-clear'
                    aria-hidden
                  />
                  <span>Revert</span>
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button
                type='button'
                className='snap-req-picker__opt snap-req-picker__opt--back'
                role='menuitem'
                onClick={() => setView('levels')}
              >
                ← Back
              </button>
              <div className='snap-req-picker__divider' />
              {(availableFunctions ?? []).map(fn => {
                const checked = assignedFunctionIds?.has(fn.id) ?? false;
                return (
                  <label key={fn.id} className='snap-req-picker__opt'>
                    <input
                      type='checkbox'
                      className='snap-req-picker__checkbox'
                      checked={checked}
                      onChange={() => onToggleFunction?.(fn.id)}
                    />
                    <span>{fn.display}</span>
                  </label>
                );
              })}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
